import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Clock,
  DollarSign,
  Gauge,
  Info,
  Loader2,
  Package,
  Pause,
  Play,
  RefreshCw,
  Square,
  X,
} from "lucide-react";

import {
  formatAgo,
  fullbaseProcessed,
  fullbaseProgressPercent,
  isFullbaseProgressUnknown,
  fullbaseStageLabel,
  formatDuration,
  formatUsdRange,
  listCustomPackages,
  type CustomPackagesResponse,
  type FullbaseEstimate,
  type FullbaseInputs,
  type FullbaseStatus,
  type ReprocessResult,
} from "@/api/fullbase";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useEstimateFullbase,
  useFullbaseControl,
  useFullbaseStatus,
  useReprocessPackages,
  useStartFullbase,
} from "@/hooks/useFullbase";
import { useWricefByPackage } from "@/hooks/useWricefCatalog";

const ACTIVE = new Set([
  "queued",
  "estimating",
  "extracting",
  "analyzing",
  "synthesizing",
]);

export function FullbaseRunnerPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const [batchSize, setBatchSize] = useState<number>(10);
  const [parallelism, setParallelism] = useState<number>(4);
  // `light` é o default por decisão do produto (#835): a primeira execução de um
  // projeto existe para alimentar o Diagnóstico, e a leve entrega tudo o que
  // aquela tela consome — a letra WRICEF vem do padrão no fonte (#770) e o nível
  // clean core vem do próprio fonte (#778), ambos sem modelo.
  //
  // A profunda é quem paga os US$ 1.062 contra US$ 77 medidos nos 38.639 objetos
  // do IDES, e acrescenta `descricao_funcional_pt` e `scope_item_candidato`, que
  // são detalhe de catálogo e não respondem o Dashboard. Ela vira escolha
  // consciente de quem precisa desse detalhe, em vez de cobrança automática de
  // quem só queria ver a tela.
  const [analysisDepth, setAnalysisDepth] = useState<"light" | "full">("light");
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [estimate, setEstimate] = useState<FullbaseEstimate | null>(null);

  // #1785: a varredura é do projeto e lê o código do cliente da carga do ex-kit, sem conexão SAP.
  // Os pacotes do seletor saem da mesma carga, e o 409 deles diz que o projeto ainda não tem carga.
  const packagesQuery = useQuery({
    queryKey: ["custom-packages", projectId],
    queryFn: () => listCustomPackages(projectId),
    enabled: Boolean(projectId),
    staleTime: 5 * 60 * 1000, // pacotes do sistema mudam pouco
    retry: false,
  });
  const noLoad = isAxiosError(packagesQuery.error)
    && packagesQuery.error.response?.status === 409;

  const { data: status } = useFullbaseStatus(projectId);
  const start = useStartFullbase(projectId);
  const estimateM = useEstimateFullbase(projectId);
  const reprocess = useReprocessPackages(projectId);
  const { cancel, pause, resume } = useFullbaseControl(projectId);

  const st = status?.status ?? "idle";
  const isActive = ACTIVE.has(st);
  const isPaused = st === "paused";
  const isCancelling = st === "cancelling";
  const busy = isActive || isPaused || isCancelling;
  const pct = fullbaseProgressPercent(status);

  // Quando a varredura conclui, o catálogo WRICEF foi repopulado no backend.
  // Invalida as queries do catálogo pra Visão Geral / Decisões / Catálogo
  // recarregarem sozinhas, sem o usuário precisar dar refresh na tela.
  // Dispara só na transição -> completed (ref evita loop de refetch).
  const qc = useQueryClient();
  const prevStatusRef = useRef<string>(st);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = st;
    if (st === "completed" && prev !== "completed") {
      qc.invalidateQueries({ queryKey: ["wricef-catalog", projectId] });
      qc.invalidateQueries({ queryKey: ["wricef-catalog-summary", projectId] });
      qc.invalidateQueries({ queryKey: ["wricef-by-package", projectId] });
    }
  }, [st, qc, projectId]);

  // #1111: durante a varredura, cada lote que entra no catálogo recarrega as
  // abas. Só a transição para `completed` recarregava, e quem abria a tela
  // antes de o job novo gravar ficava vendo o catálogo da execução anterior
  // (no segundo cliente real, "100 objetos próprios" com 495 no catálogo).
  const resolved = status?.inventory_resolved;
  const prevResolvedRef = useRef<number | undefined>(resolved);
  useEffect(() => {
    const prev = prevResolvedRef.current;
    prevResolvedRef.current = resolved;
    if (isActive && resolved !== undefined && prev !== undefined && resolved !== prev) {
      qc.invalidateQueries({ queryKey: ["wricef-catalog", projectId] });
      qc.invalidateQueries({ queryKey: ["wricef-catalog-summary", projectId] });
      qc.invalidateQueries({ queryKey: ["wricef-by-package", projectId] });
    }
  }, [resolved, isActive, qc, projectId]);

  const inputs = (): FullbaseInputs => ({
    analysis_batch_size: batchSize,
    parallelism,
    package_filter: selectedPackages.length
      ? selectedPackages.join(",")
      : undefined,
    analysis_depth: analysisDepth,
  });

  const onEstimate = () => {
    estimateM.mutate(inputs(), { onSuccess: setEstimate });
  };
  const onRun = () => {
    setEstimate(null);
    start.mutate(inputs());
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle>{t("ABAP base scan")}</CardTitle>
            <CardDescription>
              {t("Extracts the source of the whole base and analyzes it in batches (incremental). Re-triggering continues from where it stopped.")}
            </CardDescription>
          </div>
          <StageBadge status={st} />
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* ===== Configuração (some enquanto roda) ===== */}
        {!busy && (
          <>
            <div
              className="flex flex-wrap items-end gap-3"
              data-testid="fullbase-controls"
            >
              <div className="space-y-1">
                <label className="block text-xs font-medium text-muted-foreground">
                  {t("Batch (analysis)")}
                </label>
                <Input
                  type="number"
                  min={1}
                  max={500}
                  value={batchSize}
                  onChange={(e) => setBatchSize(Number(e.target.value) || 50)}
                  className="w-24"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-muted-foreground">
                  {t("Parallelism")}
                </label>
                <Input
                  type="number"
                  min={1}
                  max={16}
                  value={parallelism}
                  onChange={(e) => setParallelism(Number(e.target.value) || 4)}
                  className="w-24"
                />
              </div>

              <div className="space-y-1">
                <label
                  className="block text-xs font-medium text-muted-foreground"
                  htmlFor="fullbase-analysis-depth"
                >
                  {t("Analysis depth")}
                </label>
                <select
                  id="fullbase-analysis-depth"
                  value={analysisDepth}
                  onChange={(e) =>
                    setAnalysisDepth(e.target.value === "light" ? "light" : "full")
                  }
                  className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="light">{t("Light — pre-sales, no per-object AI")}</option>
                  <option value="full">{t("Deep — full catalog detail")}</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-medium text-muted-foreground">
                  {t("Packages (optional)")}
                </label>
                <PackagePicker
                  packagesQuery={packagesQuery}
                  disabled={noLoad}
                  selected={selectedPackages}
                  onChange={setSelectedPackages}
                />
              </div>

              <Button
                variant="outline"
                disabled={noLoad || estimateM.isPending}
                onClick={onEstimate}
                className="gap-1.5"
              >
                {estimateM.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Gauge className="h-4 w-4" />
                )}
                {t("Estimate")}
              </Button>

              <ReprocessButton
                projectId={projectId}
                disabled={noLoad || reprocess.isPending}
                pending={reprocess.isPending}
                onReprocess={(pkgs) => reprocess.mutate(pkgs)}
              />
            </div>

            {/* Fora da linha de propósito: ela alinha pela base (`items-end`), e
                três linhas de texto dentro do bloco do seletor empurravam o
                rótulo e o seletor para cima dos vizinhos. */}
            <p className="-mt-2 text-[11px] leading-snug text-muted-foreground">
              {analysisDepth === "light"
                ? t("Skips the per-object model. WRICEF letter and clean core level still come from the source; functional description and scope item candidate are left empty.")
                : t("Runs the per-object model. Fills every catalog field, and is where the cost is.")}
            </p>

            {reprocess.data && (
              <ReprocessResultLine result={reprocess.data} />
            )}

            {reprocess.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  {t("Could not reprocess. There may already be a running scan.")}
                </AlertDescription>
              </Alert>
            )}

            {noLoad && (
              <p className="flex items-center gap-1.5 text-xs text-amber-700">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {t("The project has no ex-kit load yet. Load the data through the ex-kit to enable the scan.")}
              </p>
            )}

            <ScopeChips
              selected={selectedPackages}
              onRemove={(dc) =>
                setSelectedPackages((p) => p.filter((x) => x !== dc))
              }
              onClear={() => setSelectedPackages([])}
            />

            {/* ===== Card de estimativa (pré-run) ===== */}
            {estimate && (
              <EstimateCard
                estimate={estimate}
                onConfirm={onRun}
                confirming={start.isPending}
              />
            )}

            {estimateM.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  {t("Could not estimate. Check the SAP connection.")}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

        {/* ===== Dashboard ao vivo (enquanto roda/pausado) ===== */}
        {busy && status && (
          <LiveDashboard
            status={status}
            pct={pct}
            onPause={() => pause.mutate()}
            onResume={() => resume.mutate()}
            onCancel={() => cancel.mutate()}
            controlBusy={
              pause.isPending || resume.isPending || cancel.isPending
            }
          />
        )}

        {/* ===== Estados terminais ===== */}
        {st === "completed" && (
          <Alert className="border-emerald-200 bg-emerald-50/50">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <AlertTitle className="text-xs font-semibold">
              {t("Scan completed")}
            </AlertTitle>
            <AlertDescription className="text-xs">
              {/* #1111: o `analyzed` é o acumulado do projeto e o custo é só
                  desta varredura. Visto em 08/10/2026: "223 objetos analisados
                  · custo US$ 0.00" numa varredura que reaproveitou tudo. Com
                  inventário, o card fala só desta varredura. O reprocesso não
                  tem inventário e segue no acumulado. */}
              {status?.inventory_total
                ? t("{{resolved}} of {{total}} objects of this scan in the catalog", {
                    resolved: fullbaseProcessed(status).toLocaleString("pt-BR"),
                    total: status.inventory_total.toLocaleString("pt-BR"),
                  })
                : t("{{n}} objects analyzed", { n: status?.analyzed })}
              {status?.actual_cost_usd != null
                ? ` · ${t("this scan's cost US$ {{usd}}", { usd: status.actual_cost_usd.toFixed(2) })}`
                : ""}
              {t(". The WRICEF catalog reflects the result.")}
            </AlertDescription>
          </Alert>
        )}
        {st === "cancelled" && (
          <Alert className="border-muted">
            <Square className="h-4 w-4 text-muted-foreground" />
            <AlertDescription className="text-xs">
              {t("Scan canceled. What has already been analyzed is in the catalog; re-triggering continues from that point.")}
            </AlertDescription>
          </Alert>
        )}
        {(st === "failed" || status?.error) && st !== "paused" && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle className="text-xs font-semibold">
              {t("Scan failed")}
            </AlertTitle>
            <AlertDescription className="text-xs break-words">
              {status?.error ?? t("The scan failed.")}
            </AlertDescription>
          </Alert>
        )}
        {start.isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {t("Could not start the scan. Check the SAP connection.")}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Seletor de pacotes (DEVCLASS) por escolha, não por digitação. Lista os
 * pacotes custom reais da conexão (RFC) — mata o footgun do match-exato
 * (ZFI ≠ ZFI0). Nenhum selecionado = base inteira.
 */
function PackagePicker({
  packagesQuery,
  disabled,
  selected,
  onChange,
}: {
  packagesQuery: UseQueryResult<CustomPackagesResponse>;
  disabled: boolean;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const pkgs = packagesQuery.data?.packages ?? [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggle = (devclass: string) => {
    const next = new Set(selectedSet);
    if (next.has(devclass)) next.delete(devclass);
    else next.add(devclass);
    onChange([...next]);
  };

  const label =
    selected.length === 0
      ? t("Whole base")
      : selected.length === 1
        ? selected[0]
        : `${selected.length} packages`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-64 justify-between font-normal"
        >
          <span className="flex items-center gap-1.5 truncate">
            <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {label}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Search package…")} className="h-9" />
          <CommandList>
            {packagesQuery.isLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading system
                packages…
              </div>
            ) : packagesQuery.isError ? (
              <div className="px-3 py-4 text-xs text-destructive">
                {t("Could not read the packages from the ex-kit load.")}
              </div>
            ) : (
              <>
                <CommandEmpty>{t("No package found.")}</CommandEmpty>
                {selected.length > 0 && (
                  <CommandGroup>
                    <CommandItem
                      value="__clear__"
                      onSelect={() => onChange([])}
                      className="text-xs text-muted-foreground"
                    >
                      {t("Clear selection (run the whole base)")}
                    </CommandItem>
                  </CommandGroup>
                )}
                <CommandGroup heading={`${pkgs.length} packages`}>
                  {pkgs.map((p) => (
                    <CommandItem
                      key={p.devclass}
                      value={p.devclass}
                      onSelect={() => toggle(p.devclass)}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <Check
                          className={cn(
                            "h-3.5 w-3.5",
                            selectedSet.has(p.devclass)
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        <span className="font-mono text-xs truncate">
                          {p.devclass}
                        </span>
                      </span>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {p.object_count}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Reprocessar pacotes JÁ CATALOGADOS: reavalia in-place (com a ref data atual)
 * só os pacotes escolhidos, preservando o resto. Fonte = catálogo (by-package),
 * não o SAP — só dá pra reprocessar o que já foi analisado. É o fluxo "ajustei
 * uma regra de referência e quero ver se a decisão muda", sem re-selecionar tudo
 * nem rodar a base do zero.
 */
function ReprocessButton({
  projectId,
  disabled,
  pending,
  onReprocess,
}: {
  projectId: string;
  disabled: boolean;
  pending: boolean;
  onReprocess: (packages: string[]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const byPackage = useWricefByPackage(open ? projectId : undefined);

  // Só pacotes reais: "_SEM_PACOTE" (devclass nulo) não dá pra escopar por
  // devclass, então fica fora do reprocesso.
  const pkgs = useMemo(
    () =>
      (byPackage.data?.packages ?? []).filter(
        (p) => p.devclass !== "_SEM_PACOTE",
      ),
    [byPackage.data],
  );
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const hasCatalog = pkgs.length > 0;

  const toggle = (dc: string) => {
    const next = new Set(selectedSet);
    if (next.has(dc)) next.delete(dc);
    else next.add(dc);
    setSelected([...next]);
  };

  const confirm = () => {
    if (!selected.length) return;
    onReprocess(selected);
    setOpen(false);
    setSelected([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled || pending}
          className="gap-1.5"
          title={t("Re-evaluates already analysed packages with the current ref data (does not start over)")}
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {t("Reprocess analyzed")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t("Search cataloged package…")}
            className="h-9"
          />
          <CommandList>
            {byPackage.isLoading ? (
              <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("Loading catalog…")}
              </div>
            ) : !hasCatalog ? (
              <div className="px-3 py-4 text-xs text-muted-foreground">
                {t("No cataloged package yet. Run a scan first.")}
              </div>
            ) : (
              <>
                <CommandEmpty>{t("No package found.")}</CommandEmpty>
                <CommandGroup heading={`${pkgs.length} cataloged packages`}>
                  {pkgs.map((p) => (
                    <CommandItem
                      key={p.devclass}
                      value={p.devclass}
                      onSelect={() => toggle(p.devclass)}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <Check
                          className={cn(
                            "h-3.5 w-3.5",
                            selectedSet.has(p.devclass)
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        <span className="font-mono text-xs truncate">
                          {p.devclass}
                        </span>
                      </span>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {p.total}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
          {hasCatalog && (
            <div className="flex items-center justify-between gap-2 border-t p-2">
              <span className="text-[11px] text-muted-foreground">
                {selected.length
                  ? `${selected.length} selected`
                  : t("Select packages")}
              </span>
              <Button
                size="sm"
                className="h-7 gap-1.5 text-xs"
                disabled={!selected.length}
                onClick={confirm}
              >
                <RefreshCw className="h-3 w-3" />
                Reprocess{selected.length ? ` (${selected.length})` : ""}
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * O que o reprocesso vai fazer, em números (#786).
 *
 * Reprocessar `Z610` levou o pacote de 24 para 78 linhas: o seletor de lote lê o
 * índice de fontes e não o catálogo, então todo objeto do pacote presente no
 * índice entra, tenha ou não linha. Backfillar é útil depois de varredura
 * parcial; o que não pode é a conta maior chegar sem aviso.
 *
 * `new_from_index` nulo é "não consegui contar", e some da frase — mostrar `0`
 * afirmaria o que não se sabe.
 */
function ReprocessResultLine({ result }: { result: ReprocessResult }) {
  const reanalyzed = result.reanalyzed ?? 0;
  const added = result.new_from_index;
  const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

  return (
    <p
      data-testid="reprocess-result"
      className="flex items-start gap-1.5 text-xs text-[#596b85]"
    >
      <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        Reprocessing {reanalyzed.toLocaleString("en-US")}{" "}
        {plural(reanalyzed, "catalogued row", "catalogued rows")}
        {added != null && added > 0 && (
          <>
            {" · "}
            {added.toLocaleString("en-US")}{" "}
            {plural(added, "object", "objects")} from the source index{" "}
            {plural(added, "has", "have")} no catalogue row yet and will be added
          </>
        )}
      </span>
    </p>
  );
}

function StageBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone =
    status === "completed"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : status === "failed"
        ? "bg-red-50 text-red-800 border-red-200"
        : status === "paused"
          ? "bg-amber-50 text-amber-800 border-amber-200"
          : status === "idle" || status === "cancelled"
            ? "bg-muted text-muted-foreground border-muted"
            : "bg-blue-50 text-blue-800 border-blue-200";
  return (
    <Badge variant="outline" className={`text-xs shrink-0 ${tone}`}>
      {t(fullbaseStageLabel({ status } as never))}
    </Badge>
  );
}

/** Chips dos pacotes selecionados — sem isto, fechado o picker o consultor
 * perde a noção de QUAIS pacotes escolheu. */
function ScopeChips({
  selected,
  onRemove,
  onClear,
}: {
  selected: string[];
  onRemove: (dc: string) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  if (selected.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{t("Scope:")}</span>
      {selected.map((dc) => (
        <Badge
          key={dc}
          variant="secondary"
          className="gap-1 pr-1 font-mono text-[11px]"
        >
          {dc}
          <button
            type="button"
            aria-label={`Remove ${dc}`}
            onClick={() => onRemove(dc)}
            className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <button
        type="button"
        onClick={onClear}
        className="text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        clear
      </button>
    </div>
  );
}

/** Card de estimativa pré-run: custo (faixa) + tempo + paralelismo, com o CTA
 * de confirmar. É o "antes de gastar" que o consultor pediu. */
function EstimateCard({
  estimate,
  onConfirm,
  confirming,
}: {
  estimate: FullbaseEstimate;
  onConfirm: () => void;
  confirming: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-[#dbe3ee] bg-[#f7f9fc] p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <Metric icon={<Package className="h-4 w-4" />} label={t("Objects")}>
          {estimate.objects.toLocaleString("pt-BR")}
        </Metric>
        <Metric icon={<DollarSign className="h-4 w-4" />} label={t("Estimated cost")}>
          {formatUsdRange(estimate.est_cost_usd_min, estimate.est_cost_usd_max)}
        </Metric>
        <Metric icon={<Clock className="h-4 w-4" />} label={t("Estimated time")}>
          {formatDuration(estimate.est_seconds)}
        </Metric>
        <Metric icon={<Gauge className="h-4 w-4" />} label={t("Parallelism")}>
          {estimate.parallelism}
        </Metric>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {estimate.calibration_source === "measured"
          ? t("Estimate calibrated by this client's previous runs. Cost and time are approximate.")
          : t("Estimate based on averages (no history yet). Cost and time are approximate.")}
      </p>
      <Button onClick={onConfirm} disabled={confirming} className="gap-1.5">
        {confirming ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Play className="h-4 w-4" />
        )}
        {t("Confirm and run")}
      </Button>
    </div>
  );
}

/** Dashboard ao vivo: fase, progresso, custo realizado × estimado e os
 * controles cancelar/pausar/retomar. */
function LiveDashboard({
  status,
  pct,
  onPause,
  onResume,
  onCancel,
  controlBusy,
}: {
  status: FullbaseStatus;
  pct: number;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  controlBusy: boolean;
}) {
  const { t } = useTranslation();
  const paused = status.status === "paused";
  const cancelling = status.status === "cancelling";
  const analyzing = status.status === "analyzing";
  // Fases em que o trabalho corre em lote e o % fica chapado por minutos entre
  // commits — aí a barra ganha a faixa varrendo + a dica de cadência, pra não
  // parecer travada.
  const working = status.running && !paused && !cancelling && pct < 100;
  const progressUnknown = isFullbaseProgressUnknown(status);

  // Tick de 1s enquanto analisa, só pra o heartbeat ("último lote há Xs") crescer
  // de forma contínua entre os commits de lote (prova de vida).
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!analyzing) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [analyzing]);
  return (
    <div className="space-y-4">
      {paused && (
        <Alert className="border-amber-200 bg-amber-50/60">
          <Pause className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-xs font-semibold">{t("Paused")}</AlertTitle>
          <AlertDescription className="text-xs">
            {status.pause_reason === "auth"
              ? t("SAP/AWS credential expired. Renew and resume — nothing is lost.")
              : t("Resume whenever you want; the scan continues from that point.")}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            {working && (
              <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
            )}
            {t(fullbaseStageLabel(status))}
          </span>
          <span className="font-mono tabular-nums">
            {/* #853: o denominador é o inventário do job, que não se move. Era
                o `extracted_programs`, que cresce durante a extração: a tela
                dizia `0 / 1468` e, uma hora depois, `0 / 4594`, com 0% nas
                duas. Enquanto o discover conta, não há denominador e dizer isso
                é mais honesto que mostrar um. */}
            {progressUnknown
              ? t("counting the scope")
              : t("{{processed}} of {{total}} in the catalog", {
                  processed: fullbaseProcessed(status).toLocaleString("pt-BR"),
                  total: (status.inventory_total ?? 0).toLocaleString("pt-BR"),
                })}
          </span>
        </div>
        {/* Barra: % real + faixa indeterminada varrendo enquanto trabalha, pra
            comunicar atividade mesmo quando o número fica parado entre lotes. */}
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: progressUnknown ? "0%" : `${pct}%` }}
          />
          {working && (
            <div className="absolute inset-y-0 left-0 w-1/3 animate-fb-sweep bg-gradient-to-r from-transparent via-white/70 to-transparent" />
          )}
        </div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
          {/* #853: "in scope" apontava para o `total_discovered`, que é a
              contagem crua da TADIR — inclui DTEL/TABL/TRAN, que nunca são
              analisados. Os dois números aparecem, com nomes diferentes. */}
          <span>
            {status.inventory_total
              ? t("{{inventory}} to process, of {{discovered}} found in the source", {
                  inventory: status.inventory_total.toLocaleString("pt-BR"),
                  discovered: (status.total_discovered ?? 0).toLocaleString("pt-BR"),
                }) +
                // O que separa "processado" de "analisado". Sem dizê-lo, a
                // diferença entre os dois números vira mistério na tela.
                (status.inventory_no_source
                  ? " · " +
                    t("{{n}} with no source to read", {
                      n: status.inventory_no_source.toLocaleString("pt-BR"),
                    })
                  : "")
              : status.total_discovered
                ? t("{{discovered}} found in the source", {
                    discovered: status.total_discovered.toLocaleString("pt-BR"),
                  })
                : ""}
          </span>
          <span>{progressUnknown ? "—" : `${pct}%`}</span>
        </div>
        {analyzing && (
          <div className="flex items-center justify-between gap-2 text-[11px] leading-snug">
            <p className="text-muted-foreground/80">
              {t("Analyzing in batches via AI — each batch takes a few minutes; the bar advances as each batch commits. You can leave it running.")}
            </p>
            <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
              {status.last_batch_at
                ? t("last batch {{ago}}", { ago: formatAgo(status.last_batch_at, now) })
                : t("first batch in progress…")}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <Metric icon={<DollarSign className="h-4 w-4" />} label={t("Cost so far")}>
          {status.actual_cost_usd != null
            ? `US$ ${status.actual_cost_usd.toFixed(2)}`
            : "—"}
          {status.est_cost_usd_max != null && (
            <span className="text-[11px] text-muted-foreground">
              {" "}
              {t("of {{range}}", {
                range: formatUsdRange(status.est_cost_usd_min, status.est_cost_usd_max),
              })}
            </span>
          )}
        </Metric>
        <Metric icon={<Clock className="h-4 w-4" />} label={t("Estimated time")}>
          {formatDuration(status.est_seconds)}
        </Metric>
        {/* #853 defeito 5: o paralelismo pedido não acontece dentro de um job.
            Medido em 16/09/2026 na fase de análise: os lotes são estritamente
            sequenciais — cada um começa 100 ms depois de o anterior terminar, e
            `count(*) FILTER (WHERE status='running')` devolve 1 enquanto o
            campo diz 4. Mostrá-lo ao lado do custo real afirmava que a
            varredura estava rodando em quatro frentes. O campo de configuração
            continua, e o que ele de fato controla é assunto da própria #853. */}
      </div>

      <div className="flex items-center gap-2">
        {paused ? (
          <Button
            size="sm"
            onClick={onResume}
            disabled={controlBusy}
            className="gap-1.5"
          >
            <Play className="h-3.5 w-3.5" /> {t("Resume")}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={onPause}
            disabled={controlBusy || cancelling}
            className="gap-1.5"
          >
            <Pause className="h-3.5 w-3.5" /> {t("Pause")}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          disabled={controlBusy || cancelling}
          className="gap-1.5 text-destructive hover:text-destructive"
        >
          {cancelling ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Square className="h-3.5 w-3.5" />
          )}
          {cancelling ? t("Canceling…") : t("Cancel")}
        </Button>
      </div>
    </div>
  );
}

function Metric({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground">{icon}</span>
      <span>
        <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <span className="font-semibold text-foreground">{children}</span>
      </span>
    </div>
  );
}
